import {
    ArrowLeftEndOnRectangleIcon,
    ShieldCheckIcon,
    XMarkIcon,
} from "@heroicons/react/24/outline";
import { FormattedMessage } from "react-intl";
import { router, useForm } from "@inertiajs/react";
import Button from "../common/buttons/button";
import { SubmitEvent, useState } from "react";
import { Dialog, DialogPanel, DialogTitle } from "@headlessui/react";
import Label from "../common/forms/label";
import Input from "../common/forms/input";
import Field from "../common/forms/field";
import Error from "../common/forms/error";

export default function LoginModal({ open, setOpen, slug }) {
    const [needsMfa, setNeedsMfa] = useState(false);
    const [showCodeReissued, setShowCodeReissued] = useState(false);
    const { data, setData, post, errors, clearErrors } = useForm({
        email: "",
        password: "",
        mfaCode: "",
        mfaRequired: false,
    });
    function submit(e: SubmitEvent<HTMLFormElement>) {
        e.preventDefault();
        post("/login-api", {
            onError: (e) => {
                if (e.mfaRequired === "required") {
                    clearErrors();
                    setNeedsMfa(true);
                }
            },
            onSuccess: () =>
                router.visit(route("opportunity-application", slug)),
        });
    }
    function submitMfa(e: SubmitEvent<HTMLFormElement>) {
        e.preventDefault();
        post("/mfa-api");
    }
    return (
        <Dialog open={open} onClose={setOpen} className="relative z-999">
            <div className="fixed inset-0 bg-gray-500/75" aria-hidden="true" />
            <div className="fixed inset-0 flex w-screen items-center justify-center p-8">
                <DialogPanel className="w-full sm:max-w-lg rounded-sm bg-white p-8">
                    <div className="flex items-center justify-between">
                        <DialogTitle className="text-3xl font-bold">
                            <FormattedMessage id="Log in" />
                        </DialogTitle>
                        <button
                            type="button"
                            className="rounded-md bg-white text-gray-400 hover:text-gray-500"
                            onClick={() => setOpen(false)}
                        >
                            <span className="sr-only">
                                <FormattedMessage id="Close" />
                            </span>
                            <XMarkIcon className="size-6" />
                        </button>
                    </div>
                    {!needsMfa ? (
                        <>
                            <p className="mt-2 pb-6 text-gray-900">
                                <FormattedMessage id="Welcome back! Please enter your details." />
                            </p>
                            <form onSubmit={submit}>
                                <Field>
                                    <Label>
                                        <FormattedMessage id="Email" />
                                    </Label>
                                    <Input
                                        className="block mt-1 w-full"
                                        type="email"
                                        name="email"
                                        value={data.email}
                                        onChange={(e) =>
                                            setData("email", e.target.value)
                                        }
                                        required
                                    />
                                </Field>
                                <Field className="mt-5">
                                    <Label>
                                        <FormattedMessage id="Password" />
                                    </Label>
                                    <Input
                                        className="block mt-1 w-full"
                                        type="password"
                                        name="password"
                                        value={data.password}
                                        onChange={(e) =>
                                            setData("password", e.target.value)
                                        }
                                        required
                                        autoComplete="current-password"
                                    />
                                    <Error error={errors?.password} />
                                </Field>
                                <div className="flex items-center justify-end mt-6">
                                    <div>
                                        <a
                                            className="underline underline-offset-4 text-sm text-gray-600 hover:text-gray-900"
                                            href={route("password.request")}
                                        >
                                            <FormattedMessage id="Forgot your password?" />
                                        </a>
                                    </div>
                                    <Button
                                        className="ml-3"
                                        type="submit"
                                        color="black"
                                        Icon={ArrowLeftEndOnRectangleIcon}
                                    >
                                        <FormattedMessage id="Login" />
                                    </Button>
                                </div>
                            </form>
                            <p className="mt-6 text-gray-500 text-center">
                                <FormattedMessage id="Don't have an account yet?" />{" "}
                                <button
                                    onClick={() => {
                                        localStorage.setItem(
                                            "pre-register-opp-slug",
                                            slug,
                                        );

                                        router.visit(route("register"));
                                    }}
                                    className="text-rose-600 font-semibold"
                                >
                                    <FormattedMessage id="Sign up" />
                                </button>
                            </p>
                        </>
                    ) : (
                        <>
                            <p className="mt-2 pb-6 text-gray-900">
                                <FormattedMessage id="Your account uses" />{" "}
                                <span className="font-bold">
                                    <FormattedMessage id="two-factor authentication" />
                                </span>
                                .{" "}
                                <FormattedMessage id="We have sent a unique six-digit code to your email, please check your email and enter the code below." />
                            </p>
                            <form onSubmit={submitMfa}>
                                <Field>
                                    <Label>
                                        <FormattedMessage id="Code (numbers only)" />
                                    </Label>
                                    <Input
                                        value={data.mfaCode}
                                        onChange={(e) =>
                                            setData("mfaCode", e.target.value)
                                        }
                                        className="block mt-1 w-full"
                                        type="text"
                                        name="code"
                                        required
                                    />
                                    <Error error={errors?.mfaCode} />
                                </Field>
                                {showCodeReissued && (
                                    <div className="border-l-4 border-gray-700 bg-gray-50 mt-6 mb-2 p-4">
                                        <div className="flex">
                                            <div>
                                                <p className="text-sm font-medium text-gray-900">
                                                    <FormattedMessage id="A code has been re-issued" />
                                                </p>
                                            </div>
                                            <div className="ml-auto pl-3">
                                                <div className="-mx-1.5 -my-1.5">
                                                    <button
                                                        type="button"
                                                        onClick={() =>
                                                            setShowCodeReissued(
                                                                false,
                                                            )
                                                        }
                                                        className="inline-flex bg-gray-100 rounded-md p-1.5 text-gray-900 hover:bg-gray-200"
                                                    >
                                                        <span className="sr-only">
                                                            <FormattedMessage id="Dismiss" />
                                                        </span>
                                                        <XMarkIcon className="size-5" />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                                <div className="flex items-center justify-end mt-4">
                                    <button
                                        type="button"
                                        onClick={() =>
                                            post("/resend-code", {
                                                onSuccess: () =>
                                                    setShowCodeReissued(true),
                                            })
                                        }
                                        className="underline underline-offset-4 text-sm text-gray-600 hover:text-gray-900"
                                    >
                                        <FormattedMessage id="Resend code?" />
                                    </button>
                                    <Button
                                        className="ml-3"
                                        type="submit"
                                        color="black"
                                        Icon={ShieldCheckIcon}
                                    >
                                        <FormattedMessage id="Validate" />
                                    </Button>
                                </div>
                            </form>
                        </>
                    )}
                </DialogPanel>
            </div>
        </Dialog>
    );
}
